import { useState } from 'react';
import { getCookie } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export const useAdvertisementEventServices = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const apiCall = async (url, options = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = getCookie('token');
      
      // Build headers - don't set Content-Type if body is FormData (browser will set it with boundary)
      const headers = { ...options.headers };
      
      // Only set Content-Type if it's not FormData and not already set
      if (!(options.body instanceof FormData) && !headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
      }
      
      // Always add Authorization if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}${url}`, {
        ...options,
        headers,
        credentials: 'include',
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Something went wrong');
      }
      
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getPublicAdvertisementEvents = async (params = {}) => {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      limit: params.limit || 10,
      upcoming: 'true',
      ...params
    });
    return await apiCall(`/api/v1/advertisement-events/public?${queryParams}`);
  };

  const getFeaturedAdvertisementEvent = async () => {
    return await apiCall('/api/v1/advertisement-events/featured');
  };

  const getCalendarEvents = async (year, month) => {
    return await apiCall(`/api/v1/advertisement-events/calendar?year=${year}&month=${month}`);
  };

  const getAdvertisementEventById = async (id) => {
    return await apiCall(`/api/v1/advertisement-events/${id}`);
  };

  // Admin functions
  const getAllAdvertisementEvents = async (params = {}) => {
    const queryParams = new URLSearchParams();
    
    // Add pagination
    queryParams.append('page', params.page || 1);
    queryParams.append('limit', params.limit || 10);
    
    // Only add other params if they have values (not empty strings or undefined)
    Object.keys(params).forEach(key => {
      if (key !== 'page' && key !== 'limit' && params[key] !== undefined && params[key] !== null && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    
    return await apiCall(`/api/v1/advertisement-events?${queryParams}`);
  };

  const createAdvertisementEvent = async (eventData, imageFile = null) => {
    const formData = new FormData();
    Object.keys(eventData).forEach(key => {
      if (eventData[key] !== null && eventData[key] !== undefined) {
        // Convert boolean values to strings for FormData (FormData converts them to strings anyway, but be explicit)
        const value = typeof eventData[key] === 'boolean' ? String(eventData[key]) : eventData[key];
        formData.append(key, value);
      }
    });
    if (imageFile) {
      formData.append('imageFile', imageFile);
    }
    
    return await apiCall('/api/v1/advertisement-events', {
      method: 'POST',
      body: formData
      // Don't set headers - apiCall will handle Authorization and let browser set Content-Type for FormData
    });
  };

  const updateAdvertisementEvent = async (id, eventData, imageFile = null) => {
    const formData = new FormData();
    Object.keys(eventData).forEach(key => {
      if (eventData[key] !== null && eventData[key] !== undefined) {
        formData.append(key, eventData[key]);
      }
    });
    if (imageFile) {
      formData.append('imageFile', imageFile);
    }
    
    return await apiCall(`/api/v1/advertisement-events/${id}`, {
      method: 'PUT',
      body: formData
      // Don't set headers - apiCall will handle Authorization and let browser set Content-Type for FormData
    });
  };

  const toggleEventStatus = async (id) => {
    return await apiCall(`/api/v1/advertisement-events/${id}/toggle-status`, {
      method: 'PATCH'
    });
  };

  const toggleFeaturedStatus = async (id) => {
    return await apiCall(`/api/v1/advertisement-events/${id}/toggle-featured`, {
      method: 'PATCH'
    });
  };

  const deleteAdvertisementEvent = async (id) => {
    return await apiCall(`/api/v1/advertisement-events/${id}`, {
      method: 'DELETE'
    });
  };

  return {
    getPublicAdvertisementEvents,
    getFeaturedAdvertisementEvent,
    getCalendarEvents,
    getAdvertisementEventById,
    getAllAdvertisementEvents,
    createAdvertisementEvent,
    updateAdvertisementEvent,
    toggleEventStatus,
    toggleFeaturedStatus,
    deleteAdvertisementEvent,
    loading,
    error
  };
};

